<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { FormFieldDef } from '@/types/page'
import { PAGE_DEFINITIONS, pageById } from '@/data/registry'
import { PROTOTYPE_METADATA } from '@/data/prototype-metadata'
import { useAuthStore } from '@/stores/auth'
import { usePrototypeStore } from '@/stores/prototype'
import { useUiStore } from '@/stores/ui'
import { adapter, type PlatformSettings } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { ApiError } from '@/utils/errors'
import { formatDateTime } from '@/utils/format'
import { formatDateTimeInput } from '@/foundation/time'
import AppIcon from '@/components/AppIcon.vue'
import ProField from '@/components/pro/ProField.vue'
import AppDialog from '@/components/ui/AppDialog.vue'

const definition = pageById('general-settings')
if (!definition) throw new Error('系统参数页面定义缺失')

const auth = useAuthStore()
const ui = useUiStore()
const prototype = usePrototypeStore()
const isSuperAdmin = computed(() => auth.primaryRole === 'super_admin')
const canManage = computed(() => auth.has('settings.manage'))
const saving = ref(false)
const designOpen = ref(false)
const errors = reactive<Record<string, string>>({})
const savedAt = ref('')
const savedBy = ref('')

interface SettingsForm {
  platformName: string
  servicePhone: string
  serviceHours: string
  brandColor: string
  passwordMinLength: number
  sessionMinutes: number
  mfaRequired: boolean
  loginFailureLock: number
  defaultChannels: string[]
  quietStart: string
  quietEnd: string
  retryCount: number
  alertSlaMinutes: number
  ticketSlaMinutes: number
  maintenanceEnabled: boolean
  maintenanceMessage: string
  maintenanceUntil: string
}

const form = reactive<SettingsForm>({
  platformName: '',
  servicePhone: '',
  serviceHours: '',
  brandColor: '#08B578',
  passwordMinLength: 8,
  sessionMinutes: 480,
  mfaRequired: true,
  loginFailureLock: 5,
  defaultChannels: [],
  quietStart: '21:30',
  quietEnd: '07:30',
  retryCount: 3,
  alertSlaMinutes: 30,
  ticketSlaMinutes: 120,
  maintenanceEnabled: false,
  maintenanceMessage: '',
  maintenanceUntil: '',
})

const snapshot = ref('')
const dirty = computed(() => JSON.stringify(form) !== snapshot.value)
const channelOptions = ['站内信', '短信', '微信服务号', '电话'].map(value => ({ value, label: value }))

const platformFields: FormFieldDef[] = [
  { key: 'platformName', label: '平台名称', type: 'text', required: true, maxLength: 30 },
  { key: 'servicePhone', label: '客服热线', type: 'text', required: true, maxLength: 24 },
  { key: 'serviceHours', label: '服务时间', type: 'text', required: true, maxLength: 40 },
  { key: 'brandColor', label: '品牌主色', type: 'text', pattern: '^#[0-9A-Fa-f]{6}$', patternMessage: '请输入 #RRGGBB 格式色值' },
]

const securityFields: FormFieldDef[] = [
  { key: 'passwordMinLength', label: '密码最小长度', type: 'number', required: true, min: 8, max: 32 },
  { key: 'sessionMinutes', label: '会话有效期（分钟）', type: 'number', required: true, min: 15, max: 1440 },
  { key: 'loginFailureLock', label: '失败锁定阈值（次）', type: 'number', required: true, min: 3, max: 10 },
  { key: 'retryCount', label: '任务重试次数', type: 'number', required: true, min: 0, max: 10 },
]

const notificationFields: FormFieldDef[] = [
  { key: 'defaultChannels', label: '默认通知渠道', type: 'multiselect', options: channelOptions, required: true },
  { key: 'quietStart', label: '免打扰开始', type: 'text', pattern: '^\\d{2}:\\d{2}$', patternMessage: '使用 HH:mm 格式' },
  { key: 'quietEnd', label: '免打扰结束', type: 'text', pattern: '^\\d{2}:\\d{2}$', patternMessage: '使用 HH:mm 格式' },
]

const slaFields: FormFieldDef[] = [
  { key: 'alertSlaMinutes', label: '预警响应 SLA（分钟）', type: 'number', required: true, min: 5, max: 1440 },
  { key: 'ticketSlaMinutes', label: '工单响应 SLA（分钟）', type: 'number', required: true, min: 15, max: 2880 },
]

function applySettings(settings: PlatformSettings): void {
  Object.assign(form, {
    platformName: settings.platformName,
    servicePhone: settings.servicePhone,
    serviceHours: settings.serviceHours,
    brandColor: settings.brandColor,
    passwordMinLength: settings.passwordMinLength,
    sessionMinutes: settings.sessionMinutes,
    mfaRequired: settings.mfaRequired,
    loginFailureLock: settings.loginFailureLock,
    defaultChannels: [...settings.defaultChannels],
    quietStart: settings.quietStart,
    quietEnd: settings.quietEnd,
    retryCount: settings.retryCount,
    alertSlaMinutes: settings.alertSlaMinutes,
    ticketSlaMinutes: settings.ticketSlaMinutes,
    maintenanceEnabled: settings.maintenanceEnabled,
    maintenanceMessage: settings.maintenanceMessage,
    maintenanceUntil: formatDateTimeInput(settings.maintenanceUntil),
  })
  savedAt.value = settings.updatedAt
  savedBy.value = settings.updatedBy
  snapshot.value = JSON.stringify(form)
}

function validate(): boolean {
  for (const key of Object.keys(errors)) delete errors[key]
  const rules: Array<[string, boolean, string]> = [
    ['platformName', !form.platformName.trim(), '请填写平台名称'],
    ['servicePhone', !form.servicePhone.trim(), '请填写客服热线'],
    ['serviceHours', !form.serviceHours.trim(), '请填写服务时间'],
    ['brandColor', !/^#[0-9A-Fa-f]{6}$/.test(form.brandColor), '请输入 #RRGGBB 格式色值'],
    ['passwordMinLength', Number(form.passwordMinLength) < 8, '密码最小长度不能小于8位'],
    ['sessionMinutes', Number(form.sessionMinutes) < 15, '会话有效期不能小于15分钟'],
    ['alertSlaMinutes', Number(form.alertSlaMinutes) <= 0, '预警 SLA 必须大于0分钟'],
    ['ticketSlaMinutes', Number(form.ticketSlaMinutes) <= 0, '工单 SLA 必须大于0分钟'],
    ['maintenanceMessage', form.maintenanceEnabled && !form.maintenanceMessage.trim(), '启用维护模式时必须填写维护说明'],
  ]
  for (const [key, invalid, message] of rules) {
    if (invalid) errors[key] = message
  }
  return Object.keys(errors).length === 0
}

async function save(): Promise<void> {
  if (!canManage.value || saving.value) return
  if (!validate()) {
    ElMessage.warning('请先修正标注的配置项')
    return
  }
  saving.value = true
  try {
    const result = await request(
      () => adapter.updateSettings({ ...form, defaultChannels: [...form.defaultChannels] }, auth.profile?.name ?? '管理员'),
      { write: true, delay: 420, useSimulation: true },
    )
    applySettings(result)
    ElMessage.success('系统参数已保存到当前原型内存')
  } catch (caught) {
    if (caught instanceof ApiError && caught.fieldErrors) Object.assign(errors, caught.fieldErrors)
    ElMessage.error(caught instanceof Error ? caught.message : '保存失败，请重试')
  } finally {
    saving.value = false
  }
}

function reset(): void {
  applySettings(adapter.settings)
  ElMessage.info('已恢复为最近一次保存的配置')
}

function toggleMfa(value: boolean): void {
  form.mfaRequired = value
}

function beforeUnload(event: BeforeUnloadEvent): void {
  if (!dirty.value) return
  event.preventDefault()
  event.returnValue = ''
}

onBeforeRouteLeave(async () => {
  if (!dirty.value) return true
  try {
    await ElMessageBox.confirm('系统参数有未保存的修改，离开将放弃这些内容。', '放弃未保存的修改', {
      type: 'warning',
      confirmButtonText: '放弃修改',
      cancelButtonText: '继续编辑',
      distinguishCancelAndClose: true,
    })
    return true
  } catch {
    return false
  }
})

onMounted(() => {
  applySettings(adapter.settings)
  window.addEventListener('beforeunload', beforeUnload)
})

onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))
</script>

<template>
  <section class="settings-page">
    <header class="page-header">
      <div class="page-header__text">
        <div class="page-header__title-row">
          <h1 class="page-title">系统参数</h1>
          <span class="prototype-pill">原型数据</span>
          <span v-if="dirty" class="tag tag--warning">未保存</span>
        </div>
        <p class="page-subtitle">{{ definition.purpose }}</p>
      </div>
      <div class="page-header__actions">
        <button type="button" class="btn" :disabled="!dirty || saving" @click="reset">恢复已保存</button>
        <button type="button" class="btn btn--primary" :disabled="!canManage || saving" @click="save">
          <span v-if="saving" class="button-spinner" aria-hidden="true" />
          {{ saving ? '保存中…' : '保存设置' }}
        </button>
      </div>
    </header>

    <div v-if="form.maintenanceEnabled" class="info-banner info-banner--warn" style="margin-bottom: 12px" role="status">
      <AppIcon name="wrench" :size="14" />
      <span>维护模式已启用：登录页与业务写入会提示维护窗口。<RouterLink to="/maintenance">查看维护页</RouterLink></span>
    </div>

    <div class="settings-layout">
      <div>
        <section class="settings-section card">
          <div class="settings-section__head">
            <div>
              <h2 class="settings-section__title">平台信息</h2>
              <p class="settings-section__sub">影响登录页、顶栏与用户端服务说明</p>
            </div>
            <span class="tag tag--neutral">仅原型内存</span>
          </div>
          <div class="settings-section__body settings-form-grid">
            <ProField v-model="form.platformName" :field="platformFields[0]!" :error="errors.platformName" :disabled="!canManage" />
            <ProField v-model="form.servicePhone" :field="platformFields[1]!" :error="errors.servicePhone" :disabled="!canManage" />
            <ProField v-model="form.serviceHours" :field="platformFields[2]!" :error="errors.serviceHours" :disabled="!canManage" />
            <ProField v-model="form.brandColor" :field="platformFields[3]!" :error="errors.brandColor" :disabled="!canManage" />
          </div>
        </section>

        <section class="settings-section card">
          <div class="settings-section__head">
            <div>
              <h2 class="settings-section__title">登录与会话安全</h2>
              <p class="settings-section__sub">正式环境必须由服务端校验，原型仅展示策略效果</p>
            </div>
          </div>
          <div class="settings-section__body settings-form-grid">
            <ProField v-model="form.passwordMinLength" :field="securityFields[0]!" :error="errors.passwordMinLength" :disabled="!canManage" />
            <ProField v-model="form.sessionMinutes" :field="securityFields[1]!" :error="errors.sessionMinutes" :disabled="!canManage" />
            <ProField v-model="form.loginFailureLock" :field="securityFields[2]!" :error="errors.loginFailureLock" :disabled="!canManage" />
            <ProField v-model="form.retryCount" :field="securityFields[3]!" :error="errors.retryCount" :disabled="!canManage" />
            <label class="checkbox-line span-2" style="grid-column: span 2">
              <el-switch :model-value="form.mfaRequired" :disabled="!canManage" @update:model-value="toggleMfa(Boolean($event))" />
              <span>高风险角色要求双因素认证（原型开关）</span>
            </label>
          </div>
        </section>

        <section class="settings-section card">
          <div class="settings-section__head">
            <div>
              <h2 class="settings-section__title">通知与免打扰</h2>
              <p class="settings-section__sub">用户消息与运营通知的默认渠道</p>
            </div>
          </div>
          <div class="settings-section__body settings-form-grid">
            <ProField v-model="form.defaultChannels" :field="notificationFields[0]!" :options="channelOptions" :error="errors.defaultChannels" :disabled="!canManage" />
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px">
              <ProField v-model="form.quietStart" :field="notificationFields[1]!" :error="errors.quietStart" :disabled="!canManage" />
              <ProField v-model="form.quietEnd" :field="notificationFields[2]!" :error="errors.quietEnd" :disabled="!canManage" />
            </div>
          </div>
        </section>

        <section class="settings-section card">
          <div class="settings-section__head">
            <div>
              <h2 class="settings-section__title">服务 SLA</h2>
              <p class="settings-section__sub">预警与客服工单的响应时限</p>
            </div>
          </div>
          <div class="settings-section__body settings-form-grid">
            <ProField v-model="form.alertSlaMinutes" :field="slaFields[0]!" :error="errors.alertSlaMinutes" :disabled="!canManage" />
            <ProField v-model="form.ticketSlaMinutes" :field="slaFields[1]!" :error="errors.ticketSlaMinutes" :disabled="!canManage" />
          </div>
        </section>

        <section class="settings-section card">
          <div class="settings-section__head">
            <div>
              <h2 class="settings-section__title">维护模式</h2>
              <p class="settings-section__sub">启用后显示维护页，保留已登录会话与筛选上下文</p>
            </div>
          </div>
          <div class="settings-section__body">
            <label class="checkbox-line">
              <el-switch :model-value="form.maintenanceEnabled" :disabled="!canManage" @update:model-value="form.maintenanceEnabled = Boolean($event)" />
              <span>启用维护窗口</span>
            </label>
            <div style="margin-top: 10px">
              <ProField v-model="form.maintenanceMessage" :field="{ key: 'maintenanceMessage', label: '维护说明', type: 'textarea', required: form.maintenanceEnabled, full: true, maxLength: 200 }" :error="errors.maintenanceMessage" :disabled="!canManage" />
            </div>
            <div style="margin-top: 10px">
              <ProField v-model="form.maintenanceUntil" :field="{ key: 'maintenanceUntil', label: '预计恢复时间', type: 'datetime', help: '按 Asia/Shanghai 时区展示', full: true }" :error="errors.maintenanceUntil" :disabled="!canManage" />
            </div>
          </div>
        </section>

        <section v-if="isSuperAdmin" id="prototype-dev-mode" class="settings-section card">
          <div class="settings-section__head">
            <div>
              <h2 class="settings-section__title">原型开发模式</h2>
              <p class="settings-section__sub">仅超级管理员可见，用于检查页面元素、当前页面、整个后台与实时任务进度</p>
            </div>
            <span class="tag" :class="prototype.settings.prototypeDevMode ? 'tag--success' : 'tag--neutral'">{{ prototype.settings.prototypeDevMode ? '已开启' : '已关闭' }}</span>
          </div>
          <div class="settings-section__body">
            <label class="checkbox-line">
              <el-switch :model-value="prototype.settings.prototypeDevMode" aria-label="原型开发模式" @update:model-value="prototype.setDevMode(Boolean($event))" />
              <span>开启原型开发模式面板</span>
            </label>
            <p class="muted" style="margin-top: 8px; font-size: 11px; line-height: 1.6">面板只展示当前 Vue 原型内的页面元素、当前页面、整个后台与实时任务进度；正式开发能力只在两端原型验收并确认进入正式开发后由平台工作台提供。</p>
          </div>
        </section>

        <section id="about" class="settings-section card">
          <div class="settings-section__head">
            <div>
              <h2 class="settings-section__title">关于与版本</h2>
              <p class="settings-section__sub">版本号仅在本区域展示</p>
            </div>
          </div>
          <div class="settings-section__body">
            <div class="detail-grid">
              <div class="detail-item"><span class="detail-item__label">原型版本</span><div class="detail-item__value">V{{ PROTOTYPE_METADATA.version }}</div></div>
              <div class="detail-item"><span class="detail-item__label">参考来源</span><div class="detail-item__value">reference-1 至 reference-5（{{ PROTOTYPE_METADATA.references.length }} 张管理端参考图）</div></div>
              <div class="detail-item"><span class="detail-item__label">交互覆盖</span><div class="detail-item__value">{{ PROTOTYPE_METADATA.interactionCount }} 项交互条目 · {{ PAGE_DEFINITIONS.length }} 个页面</div></div>
              <div class="detail-item"><span class="detail-item__label">正式开发</span><div class="detail-item__value"><span class="tag tag--warning tag--plain">正式开发尚未开始</span></div></div>
            </div>
            <p class="muted" style="margin-top: 10px">当前交付为参考驱动的 Vue 3 + TypeScript 管理端高保真交互原型；正式开发尚未开始，页面不代表正式部署或生产数据。</p>
            <div class="page-toolbar" style="margin-top: 8px">
              <RouterLink to="/settings/operations" class="btn btn--sm">查看运维与故障注入</RouterLink>
              <button type="button" class="btn btn--sm" @click="designOpen = true">设计说明</button>
            </div>
          </div>
        </section>

        <div class="settings-savebar">
          <span class="settings-savebar__hint">{{ dirty ? '有未保存修改，离开本页会提示确认。' : '当前配置已保存到原型内存。' }}</span>
          <button type="button" class="btn" :disabled="!dirty || saving" @click="reset">取消修改</button>
          <button type="button" class="btn btn--primary" :disabled="!canManage || saving" @click="save">{{ saving ? '保存中…' : '保存设置' }}</button>
        </div>
      </div>

      <aside class="settings-nav card card--pad">
        <div class="settings-nav__item is-active"><AppIcon name="settings" :size="14" />平台信息</div>
        <div class="settings-nav__item"><AppIcon name="lock" :size="14" />登录与会话安全</div>
        <div class="settings-nav__item"><AppIcon name="bell" :size="14" />通知与免打扰</div>
        <div class="settings-nav__item"><AppIcon name="clock" :size="14" />服务 SLA</div>
        <div class="settings-nav__item"><AppIcon name="wrench" :size="14" />维护模式</div>
        <div class="settings-nav__item"><AppIcon name="info" :size="14" />关于与版本</div>
        <div class="divider" style="margin: 10px 0" />
        <p class="faint" style="margin: 0 8px; line-height: 1.6">系统参数修改不会改变角色权限；权限与数据范围请在角色权限页维护。</p>
      </aside>
    </div>

    <AppDialog v-model="designOpen" title="设计说明" subtitle="康护健康运营工作台 · 参考驱动的 Universal Admin 原型" width="560px">
      <div class="design-note">
        <p>品牌主色取自 <code>#08B578</code>，以 8px 间距节奏、12px 卡片圆角和语义状态色组织高密度运营信息。</p>
        <ul>
          <li>参考图决定布局、色彩、圆角、密度与组件组合；业务蓝图决定页面、字段、状态和操作。</li>
          <li>筛选、分页与表单枚举复用统一可访问下拉组件，表格与表单消费同一份页面定义。</li>
          <li>1440 / 1280 / 1024 为桌面交付宽度，768 / 390 使用可关闭侧栏抽屉与局部表格滚动。</li>
          <li>所有写操作只修改当前浏览器内存；不连接正式认证、业务接口或数据服务。</li>
        </ul>
      </div>
      <template #footer>
        <button type="button" class="btn btn--primary" @click="designOpen = false">知道了</button>
      </template>
    </AppDialog>
  </section>
</template>

<style scoped>
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
</style>


<style scoped>
.design-note { color: var(--c-text); font-size: 12px; line-height: 1.75; }
.design-note p { margin: 0 0 8px; }
.design-note code { padding: 1px 5px; border-radius: 5px; background: var(--c-brand-soft); color: var(--c-brand-strong); font-family: var(--font-num); }
.design-note ul { margin: 0; padding-left: 18px; color: var(--c-muted); }
.design-note li { margin: 5px 0; }
</style>
